import { useMemo, useState } from "react";
import { RefreshCw, AlertCircle, Star } from "lucide-react";
import { useNovexData } from "@/hooks/useNovexData";
import { useLang } from "@/context/LanguageContext";
import { ExplainButton } from "@/components/ExplainButton";
import { SourceBadge } from "@/components/SourceBadge";
import { useWatchlist } from "@/context/WatchlistContext";
import { watchedCcys } from "@/lib/assets";

const IMPACT_COLOR = { high: "#EF4444", medium: "#F59E0B", low: "#64748B", holiday: "#3B82F6" };
const IMPACT_KEY = { high: "cal.high", medium: "cal.medium", low: "cal.low", holiday: "cal.holiday" };
const IMPACT_RANK = { high: 0, medium: 1, low: 2, holiday: 3 };
const FLAG = { USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", JPY: "🇯🇵", CAD: "🇨🇦", AUD: "🇦🇺", NZD: "🇳🇿", CHF: "🇨🇭", CNY: "🇨🇳" };

const num = (v) => (v === null || v === undefined || v === "" ? null : v);
const toFloat = (v) => {
  if (v == null) return null;
  const f = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
  return isNaN(f) ? null : f;
};
const iso = (d) => d.toISOString().slice(0, 10);

function ImpactDot({ tier, t }) {
  const color = IMPACT_COLOR[tier] || IMPACT_COLOR.low;
  const label = t(IMPACT_KEY[tier] || "cal.low");
  return <span className="h-2 w-2 rounded-full" style={{ background: color }} title={label} />;
}

function SurpriseBadge({ actual, consensus, t }) {
  const fa = toFloat(actual), fc = toFloat(consensus);
  if (fa == null || fc == null) return null;
  const denom = Math.abs(fc) > 1e-9 ? Math.abs(fc) : (Math.abs(fa) > 1e-9 ? Math.abs(fa) : 1);
  const dev = (fa - fc) / denom * 100;
  const mag = Math.abs(dev);
  if (mag < 1) return <span className="text-[9px] font-medium text-slate-500" title={t("cal.inlineTitle")}>{t("cal.inline")}</span>;
  const strong = mag >= 15;
  const up = dev > 0;
  const cls = up
    ? (strong ? "bg-[#10B981]/25 text-[#10B981] ring-1 ring-[#10B981]/50" : "bg-[#10B981]/10 text-[#10B981]")
    : (strong ? "bg-[#EF4444]/25 text-[#EF4444] ring-1 ring-[#EF4444]/50" : "bg-[#EF4444]/10 text-[#EF4444]");
  return (
    <span data-testid="surprise-badge" className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-bold ${cls}`} title={t("cal.surpriseTitle", { v: `${up ? "+" : ""}${dev.toFixed(1)}` })}>
      {up ? "▲" : "▼"} {mag >= 100 ? `${(dev / 100).toFixed(1)}×` : `${up ? "+" : ""}${dev.toFixed(0)}%`}
    </span>
  );
}

function ActualCell({ actual, consensus, t }) {
  const a = num(actual);
  if (a == null) return <span className="text-slate-600">—</span>;
  const fa = toFloat(actual), fc = toFloat(consensus);
  let cls = "text-slate-200";
  if (fa != null && fc != null) {
    if (fa > fc) cls = "rounded bg-[#10B981]/10 px-1.5 py-0.5 text-[#10B981]";
    else if (fa < fc) cls = "rounded bg-[#EF4444]/10 px-1.5 py-0.5 text-[#EF4444]";
  }
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className={`font-mono text-xs font-bold ${cls}`}>{String(actual)}</span>
      <SurpriseBadge actual={actual} consensus={consensus} t={t} />
    </div>
  );
}

export function EconomicCalendar({ compact = false }) {
  const { t, lang } = useLang();
  const loc = lang === "en" ? "en-US" : "fr-FR";
  const [range, setRange] = useState(compact ? "today" : "week");
  const [highOnly, setHighOnly] = useState(compact);
  const [selectedCcy, setSelectedCcy] = useState([]);
  const { watchlist } = useWatchlist();
  const wlCcys = watchedCcys(watchlist);

  const path = useMemo(() => {
    const today = new Date();
    const end = new Date();
    if (range === "week") end.setDate(end.getDate() + 7);
    const p = new URLSearchParams({ start: iso(today), end: iso(end), limit: "300" });
    if (highOnly) p.set("impact", "high,medium");
    return `/novex/calendar?${p.toString()}`;
  }, [range, highOnly]);

  const { status, data, error, reload, source } = useNovexData(path);
  const items = data?.items || [];

  const availableCcy = useMemo(() => {
    const order = ["USD", "EUR", "GBP", "JPY", "CHF", "CAD", "AUD", "NZD", "CNY"];
    const set = new Set();
    for (const it of items) if (it.currency_code) set.add(it.currency_code);
    return [...set].sort((a, b) => {
      const ia = order.indexOf(a), ib = order.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
    });
  }, [items]);

  const toggleCcy = (c) =>
    setSelectedCcy((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const groups = useMemo(() => {
    const todayIso = iso(new Date());
    let list = items;
    if (highOnly) list = list.filter((it) => it.impact_tier === "high" || it.impact_tier === "medium");
    if (selectedCcy.length) list = list.filter((it) => selectedCcy.includes(it.currency_code));
    if (range === "today") {
      list = list.filter((it) => {
        const d = new Date(it.release_at);
        return !isNaN(d) && iso(d) === todayIso;
      });
    }
    const g = {};
    for (const it of list) {
      const d = new Date(it.release_at);
      const key = isNaN(d) ? "—" : iso(d);
      (g[key] = g[key] || []).push(it);
    }
    Object.values(g).forEach((arr) => arr.sort((a, b) => {
      const ra = IMPACT_RANK[a.impact_tier] ?? 9;
      const rb = IMPACT_RANK[b.impact_tier] ?? 9;
      if (ra !== rb) return ra - rb;
      return new Date(a.release_at) - new Date(b.release_at);
    }));
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items, highOnly, range, selectedCcy]);

  const dayLabel = (key) => {
    const d = new Date(key + "T00:00:00");
    const today = iso(new Date());
    const tmr = iso(new Date(Date.now() + 86400000));
    const base = d.toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "long" });
    if (key === today) return `${t("cal.todayPrefix")} · ${base}`;
    if (key === tmr) return `${t("cal.tomorrowPrefix")} · ${base}`;
    return base;
  };
  const time = (it) => {
    if (it.is_all_day) return t("cal.allDay");
    const d = new Date(it.release_at);
    return isNaN(d) ? "--:--" : d.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <SourceBadge source={source} />
        {source === "fmp" && <span className="text-[10px] text-slate-600">{t("cal.fmpNote")}</span>}
        {source === "fallback" && <span className="text-[10px] text-slate-600">{t("cal.fredNote")}</span>}
      </div>
      {!compact && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-[#222733] bg-[#11141A] p-0.5">
            {[["today", t("cal.today")], ["week", t("cal.week")]].map(([v, l]) => (
              <button key={v} onClick={() => setRange(v)} data-testid={`cal-range-${v}`}
                className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${range === v ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => setHighOnly((h) => !h)} data-testid="cal-impact-toggle"
            className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${highOnly ? "border-red-800 bg-red-500/10 text-red-300" : "border-[#222733] bg-[#11141A] text-slate-400 hover:text-white"}`}>
            {t("cal.impactFilter")}
          </button>
          <button onClick={reload} className="ml-auto flex items-center gap-1.5 rounded-md border border-[#222733] bg-[#11141A] px-3 py-1.5 text-xs text-slate-400 hover:text-white" data-testid="cal-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> {t("cal.refresh")}
          </button>
        </div>
      )}

      {!compact && availableCcy.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5" data-testid="cal-country-filter">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t("cal.countries")}</span>
          <button
            onClick={() => setSelectedCcy([])}
            data-testid="cal-ccy-all"
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${selectedCcy.length === 0 ? "border-blue-600 bg-blue-600/20 text-blue-300" : "border-[#222733] bg-[#11141A] text-slate-400 hover:text-white"}`}>
            {t("cal.all")}
          </button>
          {wlCcys.length > 0 && (
            <button
              onClick={() => setSelectedCcy(wlCcys)}
              data-testid="cal-ccy-watchlist"
              className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${wlCcys.length && selectedCcy.length === wlCcys.length && wlCcys.every((c) => selectedCcy.includes(c)) ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-[#222733] bg-[#11141A] text-slate-400 hover:text-white"}`}>
              <Star className="h-3 w-3" /> {t("cal.watchlist")}
            </button>
          )}
          {availableCcy.map((c) => {
            const on = selectedCcy.includes(c);
            return (
              <button
                key={c}
                onClick={() => toggleCcy(c)}
                data-testid={`cal-ccy-${c}`}
                className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${on ? "border-blue-600 bg-blue-600/20 text-blue-300" : "border-[#222733] bg-[#11141A] text-slate-400 hover:text-white"}`}>
                <span>{FLAG[c] || ""}</span>{c}
              </button>
            );
          })}
        </div>
      )}

      {status === "loading" ? (
        <div className="space-y-2">{[...Array(compact ? 3 : 6)].map((_, i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-800/50" />)}</div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md border border-[#222733] bg-[#11141A] p-3 text-sm text-amber-500/80"><AlertCircle className="h-4 w-4" /> {error}</div>
      ) : groups.length === 0 ? (
        <p className="text-sm text-slate-500">{t("cal.none")}</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[#222733]">
          <div className="hidden grid-cols-12 gap-2 border-b border-[#222733] bg-[#0E1118] px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 sm:grid">
            <div className="col-span-1">{t("cal.colTime")}</div>
            <div className="col-span-1">{t("cal.colCcy")}</div>
            <div className="col-span-1 text-center">{t("cal.colImpact")}</div>
            <div className="col-span-5">{t("cal.colEvent")}</div>
            <div className="col-span-1 text-right">{t("cal.colPrev")}</div>
            <div className="col-span-1 text-right">{t("cal.colCons")}</div>
            <div className="col-span-2 text-right">{t("cal.colActual")}</div>
          </div>
          {groups.map(([key, evs]) => (
            <div key={key}>
              <div className="bg-[#161A22] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-300">{dayLabel(key)}</div>
              {(compact ? evs.slice(0, 6) : evs).map((it, i) => (
                <div key={`${it.occurrence_id}-${i}`} className="group grid grid-cols-2 items-center gap-2 border-b border-[#222733] px-4 py-2.5 text-sm transition-colors last:border-0 hover:bg-[#161A22] sm:grid-cols-12" data-testid="calendar-row">
                  <div className="col-span-1 font-mono text-xs text-slate-400">{time(it)}</div>
                  <div className="col-span-1 flex items-center gap-1 font-semibold text-slate-200">
                    <span>{FLAG[it.currency_code] || ""}</span><span className="text-xs">{it.currency_code}</span>
                  </div>
                  <div className="col-span-1 flex justify-start sm:justify-center"><ImpactDot tier={it.impact_tier} t={t} /></div>
                  <div className="col-span-2 truncate pr-2 font-medium text-slate-200 sm:col-span-5">
                    {it.display_name || it.name}
                    <span className="ml-2 hidden opacity-0 transition-opacity group-hover:opacity-100 sm:inline-flex">
                      <ExplainButton kind="événement économique" text={`${it.display_name || it.name} (${it.currency_code}). Précédent: ${it.previous}, Consensus: ${it.consensus}, Réel: ${it.actual}. Impact: ${it.impact_tier}.`} label={t("c.explain")} testid={`explain-cal-${it.occurrence_id}`} />
                    </span>
                  </div>
                  <div className="col-span-1 text-right font-mono text-xs text-slate-400">{num(it.previous) ?? "—"}</div>
                  <div className="col-span-1 text-right font-mono text-xs text-slate-300">{num(it.consensus) ?? "—"}</div>
                  <div className="col-span-2 text-right"><ActualCell actual={it.actual} consensus={it.consensus} t={t} /></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
